// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useFinancialManagementSection from 'src/hooks/use-financial-management';

// ----------------------------------------------------------------------

export default function FinancialManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const lastBenefitRef = useRef(null);
  const lastFeatureRef = useRef(null);

  const financialManagementPageData = useSelector((state) => state.website?.financialManagementPage);
  const { fetchOnMount } = useFinancialManagementSection();

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#000000",

    // Main Description
    mainDescription: "",
    mainDescriptionColor: "#666666",

    // Benefits Section
    benefitsTitle: "",
    benefitsTitleColor: "#000000",
    benefits: [
      {
        text: "",
        textColor: "#666666"
      },

    ],

    // Key Features Section
    featuresTitle: "Key Features and Integrations",
    featuresTitleColor: "#000000",
    features: [
      {
        text: "",
        textColor: "#666666"
      },

    ],

    // Bottom Text
    bottomText: "",
    bottomTextColor: "#666666",
    bottomTextStyle: "italic" // italic or normal
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateListItem = (section, itemIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) =>
        i === itemIndex ? { ...item, [field]: value } : item
      )
    }));
  };

  const addListItem = (section, refToScroll) => {
    const newItem = {
      text: "",
      textColor: "#666666"
    };

    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));

    setTimeout(() => {
      if (refToScroll && refToScroll.current) {
        refToScroll.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const removeListItem = (section, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== itemIndex)
    }));
  };

   useEffect(() => {
    fetchOnMount();
  }, []);
  useEffect(() => {
    if (financialManagementPageData?.financial_management_section) {
      // Create a deep copy of the community section to avoid read-only property issues
      const financialManagementSection = JSON.parse(JSON.stringify(financialManagementPageData.financial_management_section));
      setFormData(prev => ({
        ...prev,
        ...financialManagementSection
      }));
    }
  }, [financialManagementPageData]);


  const handleSave = async () => {
     setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website?.financial_management_page?.financial_management_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(financialManagementPageData)),
            financial_management_section: JSON.parse(JSON.stringify(formData))
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const renderListSection = (sectionTitle, titleKey, titleColorKey, listKey, items, refToScroll) => (
    <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center">
          <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
          <Typography variant="h6">{sectionTitle}</Typography>
        </Stack>
        <Button
          variant="contained"
          color="success"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={() => addListItem(listKey, refToScroll)}
        >
          Add Item
        </Button>
      </Stack>

      {/* Section Title */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label={`${sectionTitle} Title`}
            value={formData[titleKey]}
            onChange={(e) => updateFormData(titleKey, e.target.value)}
            placeholder={`Enter ${sectionTitle.toLowerCase()} title`}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Title Color"
            type="color"
            value={formData[titleColorKey]}
            onChange={(e) => updateFormData(titleColorKey, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>
      </Grid>

      {/* List Items */}
      <Grid container spacing={2}>
        {items.map((item, itemIndex) => (
          <Grid item xs={12} key={itemIndex}>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'background.neutral' }}
              ref={itemIndex === items.length - 1 ? refToScroll : null}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography variant="caption" color="primary">
                  Item {itemIndex + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeListItem(listKey, itemIndex)}
                  sx={{ minWidth: 25, p: 0.2 }}
                >
                  ×
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                  <TextField
                    fullWidth
                    label="Item Text"
                    multiline
                    rows={3}
                    value={item.text}
                    onChange={(e) => updateListItem(listKey, itemIndex, 'text', e.target.value)}
                    placeholder="Enter item text"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Text Color"
                    type="color"
                    value={item.textColor}
                    onChange={(e) => updateListItem(listKey, itemIndex, 'textColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Financial Management
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Subtitle"
                multiline
                rows={2}
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Main Description Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Description</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Description"
                multiline
                rows={4}
                value={formData.mainDescription}
                onChange={(e) => updateFormData('mainDescription', e.target.value)}
                placeholder="Enter main description"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={formData.mainDescriptionColor}
                onChange={(e) => updateFormData('mainDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Benefits Section */}
        {renderListSection("Benefits", "benefitsTitle", "benefitsTitleColor", "benefits", formData.benefits, lastBenefitRef)}

        {/* Key Features Section */}
        {renderListSection("Key Features", "featuresTitle", "featuresTitleColor", "features", formData.features, lastFeatureRef)}

        {/* Bottom Text Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Bottom Text</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Bottom Text"
                multiline
                rows={2}
                value={formData.bottomText}
                onChange={(e) => updateFormData('bottomText', e.target.value)}
                placeholder="Enter bottom text"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={formData.bottomTextColor}
                onChange={(e) => updateFormData('bottomTextColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Text Style"
                select
                SelectProps={{ native: true }}
                value={formData.bottomTextStyle}
                onChange={(e) => updateFormData('bottomTextStyle', e.target.value)}
              >
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
